import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import useOutsideClick from './helper/UseOutsideClick';
import { useSite } from "../context/SiteContext";
import { getToken, isTokenExpired, logout } from '../utils/auth';
import Notification from "./Notification";
import { authFetch } from '../utils/authFetch';
import API_ENDPOINTS from '../apiConfig';

function Header() {
  const { selectedSite, resetContext } = useSite();
  const [dateTime, setDateTime] = useState(new Date());
  const [user, setUser] = useState(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [langOpen, setLangOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasAdminAccess, setHasAdminAccess] = useState(false);

  const userMenuRef = useRef(null);
  const langMenuRef = useRef(null);

  useOutsideClick([userMenuRef], () => setUserMenuOpen(false));
  useOutsideClick([langMenuRef], () => setLangOpen(false));

  useEffect(() => {
    const token = getToken();
    if (!token || isTokenExpired(0)) {
      logout(navigate, resetContext);
      return;
    }

    const decoded = jwtDecode(token);
    setUser(decoded.name || decoded.email);
  }, [navigate]);

  // Offer the admin portal only to users with an admin role. Purely cosmetic —
  // the portal itself requires a separate confirmed admin session.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    authFetch(API_ENDPOINTS.adminSessionStatus)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (!cancelled) setHasAdminAccess(!!data?.has_admin_access); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    setLangOpen(false);
  };

  const delToken = async () => {
    setUser(null);
    logout(navigate, resetContext);
  };

  return (
    <div className="d-flex justify-content-between align-items-center p-3 bg-white border-bottom ez-shadow">

      <div
        className="d-flex justify-content-between align-items-center w-100"
        style={{ maxWidth: "1300px", margin: "0 auto" }}
      >
        <div>
          <a
            className={`ez-logo d-inline-flex text-decoration-none ${selectedSite?.sales_partner ? "flex-column align-items-center" : "flex-row align-items-start"
              }`}
            href="https://app.ezentry.at/dashboard">
            {selectedSite?.sales_partner ? (
              <>
                <img
                  src={`/img/${selectedSite.sales_partner}.png`}
                  alt={selectedSite.sales_partner}
                  style={{ height: "40px" }}
                />
                <span className="by-ezentry">by ezentry</span>
              </>
            ) : (
              <>
                <img
                  src="/img/ezentry.svg"
                  style={{ height: "40px" }}

                />
                <span className="ez-logo-text">ezentry</span>
              </>
            )}
          </a></div>

        <div className="d-flex align-items-center gap-3">
          {/* User avatar menu */}
          {user && (
            <div ref={userMenuRef} className="user-dropdown position-relative text-center">
              <button
                className="user-avatar"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <i className="bi bi-person"></i>
                <div className="user-name">{user}</div>
              </button>

              {userMenuOpen && (
                <div className="dropdown-menu show">
                  <button className="dropdown-item" onClick={() => { navigate('/settings'); setUserMenuOpen(false); }}>
                    <i className="bi bi-gear me-2"></i>{t("Settings")}
                  </button>
                  {hasAdminAccess && (
                    <button className="dropdown-item" onClick={() => { navigate('/admin'); setUserMenuOpen(false); }}>
                      <i className="bi bi-shield-lock me-2"></i>{t("Administration")}
                    </button>
                  )}
                  <button className="dropdown-item" onClick={delToken}>
                    {t("Logout")}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Language dropdown */}
          <div ref={langMenuRef} className="lang-dropdown position-relative">
            <button
              className="ez-globe"
              onClick={() => setLangOpen(!langOpen)}
            >
              <i className="bi bi-globe"></i>
            </button>

            {langOpen && (
              <div className="dropdown-menu show">
                <button className="dropdown-item" onClick={() => changeLanguage('en')}>
                  🇬🇧 {t("en")}
                </button>
                <button className="dropdown-item" onClick={() => changeLanguage('de')}>
                  🇩🇪 {t("de")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Notification
        message={errorMessage}
        type="danger"
        onClose={() => setErrorMessage("")}
      />
    </div>
  );
}

export default Header;
